import React, { useEffect, useRef } from 'react';

/**
 * Full-bleed field of vertical lines that ripple like fabric.
 * The cursor stills the wave locally and parts the lines like a curtain.
 */
const WaveField = ({
  lineColor = '#dedbd2',
  background = '#050505',
  spacing = 13,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf;
    let w = 0;
    let h = 0;
    let t = Math.random() * 1000;
    const mouse = { x: -1e4, y: -1e4, tx: -1e4, ty: -1e4 };

    const CURTAIN_RADIUS = 150; // px around the cursor that gets interrupted
    const CURTAIN_PART = 70;    // how far lines are pushed apart

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      t += 0.014;
      mouse.x += (mouse.tx - mouse.x) * 0.12;
      mouse.y += (mouse.ty - mouse.y) * 0.12;

      ctx.fillStyle = background;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.9;

      const yStep = 6;
      ctx.beginPath();
      for (let gx = -CURTAIN_PART; gx <= w + CURTAIN_PART; gx += spacing) {
        for (let y = 0; y <= h + yStep; y += yStep) {
          // layered sines give the slow, cloth-like horizontal drift
          const wave =
            34 * Math.sin(y * 0.0042 + t * 0.9 + gx * 0.0042) +
            18 * Math.sin(y * 0.0105 - t * 0.6 + gx * 0.0021) +
            8 * Math.sin(y * 0.021 + t * 1.4 + gx * 0.008);

          const dx = gx + wave - mouse.x;
          const dy = y - mouse.y;
          const influence = Math.exp(-(dx * dx + dy * dy) / (CURTAIN_RADIUS * CURTAIN_RADIUS));

          // near the cursor: damp the wave and push lines aside like a curtain
          const x =
            gx +
            wave * (1 - influence) +
            Math.tanh(dx / 40) * CURTAIN_PART * influence;

          if (y === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
      }
      ctx.stroke();

      if (!reduceMotion) raf = requestAnimationFrame(draw);
    };

    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.tx = e.clientX - rect.left;
      mouse.ty = e.clientY - rect.top;
      if (reduceMotion) draw();
    };
    const onPointerLeave = () => {
      mouse.tx = -1e4;
      mouse.ty = -1e4;
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      if (reduceMotion) draw();
    });
    ro.observe(canvas);
    window.addEventListener('pointermove', onPointerMove);
    document.documentElement.addEventListener('mouseleave', onPointerLeave);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('mouseleave', onPointerLeave);
    };
  }, [lineColor, background, spacing]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: '100%' }}
    />
  );
};

export default WaveField;
